import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../app/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { LEGAL_PAGE_IDS } from './legalPageConstants';

const tsToMillis = (ts) => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : null);

const normalizeLegalPage = (id, data, extra = {}) => ({
  id,
  ...data,
  updatedAt: tsToMillis(data.updatedAt),
  createdAt: tsToMillis(data.createdAt),
  ...extra,
});

export const fetchLegalPages = createAsyncThunk('legalPages/fetchAll', async () => {
  const pages = await Promise.all(
    LEGAL_PAGE_IDS.map(async (id) => {
      const snap = await getDoc(doc(db, 'legalPages', id));
      if (!snap.exists()) {
        return { id, exists: false };
      }
      return { ...normalizeLegalPage(snap.id, snap.data()), exists: true };
    }),
  );
  return pages;
});

export const fetchLegalPageById = createAsyncThunk('legalPages/fetchById', async (id) => {
  const snap = await getDoc(doc(db, 'legalPages', id));
  if (!snap.exists()) {
    return { id, exists: false };
  }
  return { ...normalizeLegalPage(snap.id, snap.data()), exists: true };
});

export const saveLegalPage = createAsyncThunk(
  'legalPages/save',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const now = serverTimestamp();
      const ref = doc(db, 'legalPages', id);
      const existing = await getDoc(ref);

      const pageData = {
        slug: id,
        title: data.title || {},
        contentHtml: data.contentHtml || {},
        updatedAt: now,
        ...(existing.exists() ? {} : { createdAt: now }),
      };

      await setDoc(ref, pageData, { merge: true });

      const savedSnap = await getDoc(ref);
      return { ...normalizeLegalPage(ref.id, savedSnap.data()), exists: true };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to save legal page');
    }
  },
);

const initialState = {
  items: [],
  byId: {},
  loadingList: false,
  loadingItem: false,
  saving: false,
  error: null,
};

const legalPagesSlice = createSlice({
  name: 'legalPages',
  initialState,
  reducers: {
    resetLegalPagesState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLegalPages.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchLegalPages.fulfilled, (state, action) => {
        state.loadingList = false;
        state.items = action.payload;
        action.payload.forEach((page) => {
          state.byId[page.id] = page;
        });
      })
      .addCase(fetchLegalPages.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.error?.message || 'Failed to load legal pages';
      })

      .addCase(fetchLegalPageById.pending, (state) => {
        state.loadingItem = true;
        state.error = null;
      })
      .addCase(fetchLegalPageById.fulfilled, (state, action) => {
        state.loadingItem = false;
        const page = action.payload;
        state.byId[page.id] = page;
      })
      .addCase(fetchLegalPageById.rejected, (state, action) => {
        state.loadingItem = false;
        state.error = action.error?.message || 'Failed to load legal page';
      })

      .addCase(saveLegalPage.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveLegalPage.fulfilled, (state, action) => {
        state.saving = false;
        const page = action.payload;
        state.byId[page.id] = page;
        const index = state.items.findIndex((item) => item.id === page.id);
        if (index >= 0) {
          state.items[index] = page;
        } else {
          state.items.push(page);
        }
      })
      .addCase(saveLegalPage.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || 'Failed to save legal page';
      });
  },
});

export const { resetLegalPagesState } = legalPagesSlice.actions;

export const selectLegalPages = (state) => state.legalPages.items;
export const selectLegalPagesLoading = (state) => state.legalPages.loadingList;
export const selectLegalPageLoading = (state) => state.legalPages.loadingItem;
export const selectLegalPageSaving = (state) => state.legalPages.saving;
export const selectLegalPageError = (state) => state.legalPages.error;
export const selectLegalPageById = (id) => (state) => state.legalPages.byId[id];

export default legalPagesSlice.reducer;
